// Admin Panel - Products Page (Two Wheeler Spare Parts)
// File: admin-panel/src/pages/Products.jsx

import React, { useState, useEffect, useRef } from 'react';
import { collection, query, onSnapshot, addDoc, updateDoc, deleteDoc, doc, orderBy, getDocs, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../../services/firebase' // Make sure auth is exported
import { onAuthStateChanged } from 'firebase/auth';
import {
  CubeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  TagIcon,
  PhotoIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/outline';

// Product Modal Component
function ProductModal({ isOpen, onClose, product, onSave, categories, brands }) {
  const [formData, setFormData] = useState({
    name: '',
    partNumber: '',
    hsnCode: '',
    description: '',
    price: '',
    stock: '',
    reorderLevel: '10',
    warrantyMonths: '',
    categories: [],
    brands: [],
    modelCompatibility: [],
    imageUrls: [],
    notes: '',
    isActive: true,
    vehicleNumber: '',
  });
  const [loading, setLoading] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [brandSearch, setBrandSearch] = useState('');
  const [modelInput, setModelInput] = useState('');
  
  // Image upload states
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        partNumber: product.partNumber || product.sku || '',
        hsnCode: product.hsnCode || '',
        description: product.description || '',
        price: product.price?.toString() || '',
        stock: product.stock?.toString() || '',
        reorderLevel: product.reorderLevel?.toString() || '10',
        warrantyMonths: product.warrantyMonths?.toString() || '',
        categories: product.categories || [],
        brands: product.brands || [],
        modelCompatibility: product.modelCompatibility || [],
        imageUrls: product.imageUrls || [],
        notes: product.notes || '',
        isActive: product.isActive !== false,
        vehicleNumber: product.vehicleNumber || '',
      });
      setImagePreviews(product.imageUrls || []);
      setImageFiles([]);
    } else {
      setFormData({
        name: '',
        partNumber: '',
        hsnCode: '',
        description: '',
        price: '',
        stock: '',
        reorderLevel: '10',
        warrantyMonths: '',
        categories: [],
        brands: [],
        modelCompatibility: [],
        imageUrls: [],
        notes: '',
        isActive: true,
        vehicleNumber: '',
      });
      setImagePreviews([]);
      setImageFiles([]);
    }
  }, [product, isOpen]);

  // Filter categories based on search
  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  // Filter brands based on search
  const filteredBrands = brands.filter(brand =>
    brand.name.toLowerCase().includes(brandSearch.toLowerCase())
  );

  // Handle category selection
  const handleCategoryChange = (category, checked) => {
    if (checked) {
      setFormData({ ...formData, categories: [...formData.categories, category] });
    } else {
      setFormData({
        ...formData,
        categories: formData.categories.filter(cat => cat.id !== category.id)
      });
    }
  };

  // Handle brand selection
  const handleBrandChange = (brand, checked) => {
    if (checked) {
      setFormData({ ...formData, brands: [...formData.brands, brand.id] });
    } else {
      setFormData({
        ...formData,
        brands: formData.brands.filter(id => id !== brand.id)
      });
    }
  };

  // Handle model compatibility
  const handleAddModel = () => {
    if (modelInput.trim() && !formData.modelCompatibility.includes(modelInput.trim())) {
      setFormData({
        ...formData,
        modelCompatibility: [...formData.modelCompatibility, modelInput.trim()]
      });
      setModelInput('');
    }
  };

  const handleRemoveModel = (model) => {
    setFormData({
      ...formData,
      modelCompatibility: formData.modelCompatibility.filter(m => m !== model)
    });
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not an image file`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} is too large (max 5MB)`);
        return false;
      }
      return true;
    });

    // Create previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });

    setImageFiles(prev => [...prev, ...validFiles]);
  };

  // Remove image
  const handleRemoveImage = (index) => {
    const existingCount = formData.imageUrls.length;
    
    if (index < existingCount) {
      // Remove from existing URLs
      setFormData({
        ...formData,
        imageUrls: formData.imageUrls.filter((_, i) => i !== index)
      });
    } else {
      // Remove from new files
      const fileIndex = index - existingCount;
      setImageFiles(prev => prev.filter((_, i) => i !== fileIndex));
    }
    
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Upload images to Firebase Storage
  const uploadImages = async () => {
    if (imageFiles.length === 0) return formData.imageUrls;

    setUploadingImages(true);
    const uploadedUrls = [...formData.imageUrls];

    try {
      for (const file of imageFiles) {
        const fileName = `products/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        uploadedUrls.push(downloadURL);
      }
      return uploadedUrls;
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    } finally {
      setUploadingImages(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload images first
      const imageUrls = await uploadImages();

      const productData = {
        name: formData.name.trim(),
        partNumber: formData.partNumber.trim().toUpperCase(),
        hsnCode: formData.hsnCode.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price) || 0,
        sellingPrice: parseFloat(formData.price) || 0,
        stock: parseInt(formData.stock) || 0,
        reorderLevel: parseInt(formData.reorderLevel) || 10,
        warrantyMonths: parseInt(formData.warrantyMonths) || 0,
        categories: formData.categories,
        brands: formData.brands,
        modelCompatibility: formData.modelCompatibility,
        imageUrls: imageUrls,
        notes: formData.notes.trim(),
        isActive: formData.isActive,
        vehicleNumber: formData.vehicleNumber.trim().toUpperCase(),
        mainAdminOnly: true,
        createdByMainAdmin: true,
        updatedAt: new Date().toISOString(),
      };

      await onSave(productData);
      onClose();
    } catch (error) {
      console.error('Save product error:', error);
      alert('Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10 rounded-t-3xl">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {product ? 'Edit Product' : 'Add New Product'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">Fill in the product details below</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          
          {/* Vehicle Number Plate - Admin Only Feature */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">🚗</span>
              <label className="text-red-700 font-semibold">Vehicle Number Plate</label>
              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Admin Only</span>
            </div>
            <input
              type="text"
              value={formData.vehicleNumber}
              onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value.toUpperCase() })}
              placeholder="Enter vehicle number (e.g., KA01AB1234)"
              className="w-full px-4 py-3 bg-white border-2 border-red-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent uppercase tracking-wider font-mono text-lg"
            />
            <p className="mt-2 text-xs text-red-500">
              Enter vehicle number to fetch details (API integration coming soon)
            </p>
          </div>

          {/* Basic Info */}
          <div className="bg-gray-50 rounded-2xl p-5">
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <CubeIcon className="w-5 h-5 text-red-600" />
              </span>
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Part Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Brake Pad Set"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Part Number *</label>
                <input
                  type="text"
                  value={formData.partNumber}
                  onChange={(e) => setFormData({ ...formData, partNumber: e.target.value.toUpperCase() })}
                  placeholder="e.g., BP-HON-001"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent uppercase transition-all"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">HSN Code</label>
                <input
                  type="text"
                  value={formData.hsnCode}
                  onChange={(e) => setFormData({ ...formData, hsnCode: e.target.value })}
                  placeholder="GST classification code (4-8 digits)"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Warranty (Months)</label>
                <input
                  type="number"
                  value={formData.warrantyMonths}
                  onChange={(e) => setFormData({ ...formData, warrantyMonths: e.target.value })}
                  placeholder="0"
                  min="0"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-gray-700 text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Product description..."
                rows={3}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none transition-all"
              />
            </div>
          </div>

          {/* Image Upload Section */}
          <div className="bg-gray-50 rounded-2xl p-5">
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <PhotoIcon className="w-5 h-5 text-red-600" />
              </span>
              Product Images
            </h3>
            
            {/* Upload Area */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-red-400 hover:bg-red-50/50 transition-all"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className="hidden"
              />
              <CloudArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">Click to upload images</p>
              <p className="text-gray-400 text-sm mt-1">PNG, JPG, WEBP up to 5MB each</p>
            </div>

            {/* Image Previews */}
            {imagePreviews.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-24 object-cover rounded-xl border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                    {index === 0 && (
                      <span className="absolute bottom-1 left-1 text-xs bg-red-500 text-white px-2 py-0.5 rounded">
                        Main
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {uploadingImages && (
              <div className="mt-4 flex items-center justify-center gap-2 text-red-600">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Uploading images...</span>
              </div>
            )}
          </div>

          {/* Categories Section */}
          <div className="bg-gray-50 rounded-2xl p-5">
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <TagIcon className="w-5 h-5 text-red-600" />
              </span>
              Product Categories
            </h3>
            <div className="mb-4">
              <input
                type="text"
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                placeholder="Search categories..."
                className="w-full px-4 py-2 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
              />
            </div>
            {categories.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-48 overflow-y-auto">
                {filteredCategories.map((category) => {
                  const isSelected = formData.categories.some(cat => cat.id === category.id);
                  return (
                    <div
                      key={category.id}
                      onClick={() => handleCategoryChange(category, !isSelected)}
                      className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-red-500 bg-red-50 shadow-md'
                          : 'border-gray-200 hover:border-red-300 bg-white hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'bg-red-500 border-red-500' : 'border-gray-300'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <span className={`text-sm font-medium truncate ${isSelected ? 'text-red-700' : 'text-gray-700'}`}>
                          {category.name}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-4">No categories available</p>
            )}
            {formData.categories.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {formData.categories.map((cat, index) => (
                  <span key={index} className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full">
                    {cat.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Brands Section */}
          <div className="bg-gray-50 rounded-2xl p-5">
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </span>
              Brand Information
            </h3>
            <div className="mb-4">
              <input
                type="text"
                value={brandSearch}
                onChange={(e) => setBrandSearch(e.target.value)}
                placeholder="Search brands..."
                className="w-full px-4 py-2 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
              />
            </div>
            {brands.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-48 overflow-y-auto">
                {filteredBrands.map((brand) => {
                  const isSelected = formData.brands.includes(brand.id);
                  return (
                    <div
                      key={brand.id}
                      onClick={() => handleBrandChange(brand, !isSelected)}
                      className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-red-500 bg-red-50 shadow-md'
                          : 'border-gray-200 hover:border-red-300 bg-white hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'bg-red-500 border-red-500' : 'border-gray-300'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        {brand.logo && (
                          <img src={brand.logo} alt={brand.name} className="w-6 h-6 object-contain" />
                        )}
                        <span className={`text-sm font-medium truncate ${isSelected ? 'text-red-700' : 'text-gray-700'}`}>
                          {brand.name}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-4">No brands available</p>
            )}
            {formData.brands.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {formData.brands.map((brandId, index) => {
                  const brand = brands.find(b => b.id === brandId);
                  return (
                    <span key={index} className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full flex items-center gap-1">
                      {brand?.logo && <img src={brand.logo} alt="" className="w-4 h-4 object-contain" />}
                      {brand?.name || brandId}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Model Compatibility Section */}
          <div className="bg-gray-50 rounded-2xl p-5">
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
              </span>
              Vehicle Model Compatibility
            </h3>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={modelInput}
                onChange={(e) => setModelInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddModel())}
                placeholder="Enter compatible model (e.g., Honda Activa 6G)"
                className="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
              />
              <button
                type="button"
                onClick={handleAddModel}
                className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-medium rounded-xl transition-colors shadow-lg shadow-red-500/30"
              >
                Add
              </button>
            </div>
            {formData.modelCompatibility.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {formData.modelCompatibility.map((model, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-full shadow-sm"
                  >
                    🏍️ {model}
                    <button
                      type="button"
                      onClick={() => handleRemoveModel(model)}
                      className="w-5 h-5 bg-gray-100 hover:bg-red-100 rounded-full flex items-center justify-center text-gray-500 hover:text-red-500 transition-colors"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No compatible models added yet. Add models to help customers find this part.</p>
            )}
          </div>

          {/* Pricing & Inventory */}
          <div className="bg-gray-50 rounded-2xl p-5">
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              Pricing & Inventory
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Selling Price (₹) *</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">₹</span>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Stock Quantity *</label>
                <input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  placeholder="0"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  required
                  min="0"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Reorder Level</label>
                <input
                  type="number"
                  value={formData.reorderLevel}
                  onChange={(e) => setFormData({ ...formData, reorderLevel: e.target.value })}
                  placeholder="10"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-gray-50 rounded-2xl p-5">
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </span>
              Additional Notes
            </h3>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional remarks, installation notes, special instructions..."
              rows={3}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none transition-all"
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                formData.isActive ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                formData.isActive ? 'translate-x-7' : 'translate-x-0'
              }`} />
            </button>
            <label className="text-gray-700 font-medium">
              Product is {formData.isActive ? 'Active' : 'Inactive'}
            </label>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingImages}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading || uploadingImages ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {uploadingImages ? 'Uploading...' : 'Saving...'}
                </span>
              ) : (
                product ? 'Update Product' : 'Add Product'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Product Card Component
function ProductCard({ product, onEdit, onDelete, categories, brands }) {
  const productCategories = product.categories || [];
  const productBrands = product.brands || [];
  const mainImage = product.imageUrls?.[0] || product.imageUrl;

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 group">
      {/* Image */}
      <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-50 relative overflow-hidden">
        {mainImage ? (
          <img
            src={mainImage}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <CubeIcon className="w-20 h-20 text-gray-300" />
          </div>
        )}
        {/* Vehicle Number Badge */}
        {product.vehicleNumber && (
          <span className="absolute top-3 left-3 px-3 py-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-full shadow-lg">
            🚗 {product.vehicleNumber}
          </span>
        )}
        {/* Status Badge */}
        <span className={`absolute top-3 right-3 px-3 py-1 text-xs font-semibold rounded-full ${
          product.isActive 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          {product.isActive ? '● Active' : '○ Inactive'}
        </span>

        {/* Image count badge */}
        {product.imageUrls && product.imageUrls.length > 1 && (
          <span className="absolute bottom-3 right-3 px-2 py-1 bg-black/60 text-white text-xs rounded-lg flex items-center gap-1">
            <PhotoIcon className="w-3 h-3" />
            {product.imageUrls.length}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Categories */}
        {productCategories.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {productCategories.slice(0, 2).map((cat, index) => (
              <span key={index} className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full font-medium">
                {cat.name || cat}
              </span>
            ))}
            {productCategories.length > 2 && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                +{productCategories.length - 2}
              </span>
            )}
          </div>
        )}

        {/* Name & Part Number */}
        <h3 className="text-gray-900 font-bold text-lg mb-1 truncate group-hover:text-red-600 transition-colors">
          {product.name}
        </h3>
        <p className="text-gray-500 text-sm mb-3 font-mono">
          Part #: {product.partNumber || product.sku || 'N/A'}
        </p>

        {/* Brands */}
        {productBrands.length > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <TagIcon className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600 text-sm truncate">
              {productBrands.map(brandId => {
                const brand = brands.find(b => b.id === brandId);
                return brand?.name || brandId;
              }).join(', ')}
            </span>
          </div>
        )}

        {/* Model Compatibility */}
        {product.modelCompatibility && product.modelCompatibility.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-1">
              {product.modelCompatibility.slice(0, 2).map((model, index) => (
                <span key={index} className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                  🏍️ {model}
                </span>
              ))}
              {product.modelCompatibility.length > 2 && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  +{product.modelCompatibility.length - 2} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Price & Stock */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-2xl font-bold text-gray-900">
            ₹{product.price?.toLocaleString('en-IN')}
          </span>
          <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
            product.stock > 10 
              ? 'bg-green-100 text-green-700' 
              : product.stock > 0 
                ? 'bg-yellow-100 text-yellow-700' 
                : 'bg-red-100 text-red-700'
          }`}>
            {product.stock > 0 ? `${product.stock} in stock` : 'Out of Stock'}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(product)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-red-50 text-gray-700 hover:text-red-600 font-semibold rounded-xl transition-colors"
          >
            <PencilIcon className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={() => onDelete(product)}
            className="px-4 py-3 bg-red-50 hover:bg-red-100 text-red-500 hover:text-red-600 rounded-xl transition-colors"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);  // ADD THIS

  // Get current authenticated admin user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Load products - ONLY for current admin's products
  useEffect(() => {
    if (!currentUser) return;

    // Query products where vendorId matches current admin OR mainAdminOnly is true
    const productsQuery = query(
      collection(db, 'products'),
      where('vendorId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(productsQuery, (snapshot) => {
      const productsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(productsList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Load categories from Firebase
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesRef = collection(db, 'categories');
        const snapshot = await getDocs(categoriesRef);
        const categoriesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCategories(categoriesList);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Load brands from Firebase
  useEffect(() => {
    const loadBrands = async () => {
      try {
        const brandsRef = collection(db, 'brands');
        const snapshot = await getDocs(brandsRef);
        const brandsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setBrands(brandsList);
      } catch (error) {
        console.error('Error loading brands:', error);
      }
    };
    loadBrands();
  }, []);

  // UPDATED: Save product with vendorId
  const handleSaveProduct = async (productData) => {
    if (!currentUser) {
      alert('You must be logged in to save products');
      return;
    }

    // Add vendorId to link with Main Admin's vendor account
    const dataWithVendor = {
      ...productData,
      vendorId: currentUser.uid,  // IMPORTANT: Links to Main Admin's vendor account
      mainAdminOnly: true,
      createdByMainAdmin: true,
    };

    if (editProduct) {
      await updateDoc(doc(db, 'products', editProduct.id), dataWithVendor);
    } else {
      await addDoc(collection(db, 'products'), {
        ...dataWithVendor,
        createdAt: new Date().toISOString(),
      });
    }
    setEditProduct(null);
  };

  const handleEditProduct = (product) => {
    setEditProduct(product);
    setShowModal(true);
  };

  const handleDeleteProduct = async (product) => {
    if (window.confirm(`Delete "${product.name}"? This action cannot be undone.`)) {
      await deleteDoc(doc(db, 'products', product.id));
    }
  };

  const filteredProducts = products.filter(product => {
    const name = product.name?.toLowerCase() || '';
    const partNumber = product.partNumber?.toLowerCase() || '';
    const vehicleNumber = product.vehicleNumber?.toLowerCase() || '';
    const queryLower = searchQuery.toLowerCase();

    const matchesSearch =
      name.includes(queryLower) ||
      partNumber.includes(queryLower) ||
      vehicleNumber.includes(queryLower);

    const matchesCategory =
      selectedCategory === 'all' ||
      (product.categories && product.categories.some(cat => cat.id === selectedCategory));

    return matchesSearch && matchesCategory;
  });

  // Show loading if no user yet
  if (!currentUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
          <p className="text-gray-500 font-medium">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
          <p className="text-gray-500 font-medium">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-500 mt-1">Manage your two wheeler spare parts inventory</p>
        </div>
        <button
          onClick={() => { setEditProduct(null); setShowModal(true); }}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-red-500/30"
        >
          <PlusIcon className="w-5 h-5" />
          Add Product
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <CubeIcon className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Total Products</p>
              <p className="text-2xl font-bold text-gray-900">{products.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Active</p>
              <p className="text-2xl font-bold text-gray-900">{products.filter(p => p.isActive).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Low Stock</p>
              <p className="text-2xl font-bold text-gray-900">
                {products.filter(p => p.stock > 0 && p.stock <= (p.reorderLevel || 10)).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Out of Stock</p>
              <p className="text-2xl font-bold text-gray-900">{products.filter(p => p.stock <= 0).length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, part number, vehicle number..."
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all min-w-[200px]"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedCategory === 'all'
                ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All ({products.length})
          </button>
          {categories.map(cat => {
            const count = products.filter(p =>
              p.categories && p.categories.some(c => c.id === cat.id)
            ).length;
            if (count === 0) return null;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.name} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Products Grid */}
      {filteredProducts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              onEdit={handleEditProduct}
              onDelete={handleDeleteProduct}
              categories={categories}
              brands={brands}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-100">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CubeIcon className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Products Found</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery || selectedCategory !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Start by adding your first product'}
          </p>
          <button
            onClick={() => { setEditProduct(null); setShowModal(true); }}
            className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-red-500/30"
          >
            Add Your First Product
          </button>
        </div>
      )}

      {/* Product Modal - Pass currentUser */}
      <ProductModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditProduct(null); }}
        product={editProduct}
        onSave={handleSaveProduct}
        categories={categories}
        brands={brands}
        currentUser={currentUser}
      />
    </div>
  );
}

export default Products;